// Top banner for /home: centred wordmark with a switcher caret and settings
// on the right.
//
// It is a separate component from LeagueNavBar rather than a variant of it:
// that bar carries a league name and a back-to-home mark, this one carries the
// app wordmark, and the only thing they genuinely share is the grid and the
// profile icon.
//
// The colours live in BAR below rather than inline, because the dark bar is
// coming. Swapping the object is the whole change: the wordmark already has
// an on-dark twin, and every colour the bar draws with is named here.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import ProfileDropdown from '../ProfileDropdown';
import LeagueSwitcherSheet from './LeagueSwitcherSheet';
import TourButton from './TourButton';
import wordmarkOnLight from '../../assets/logo-wordmark-on-light.png';
import wordmarkOnDark from '../../assets/logo-wordmark-on-dark.png';

const BAR = {
  // Translucent, matching LeagueNavBar, so the two bars read as one app. The
  // token carries the alpha: an opaque fill would lose the blur entirely.
  background: 'var(--color-chrome)',
  border: '1px solid var(--color-chrome-border)',
  caret: 'var(--color-text-faint)',
};


// demo: the signed-out /demo Home. One league and no profile, so the
// switcher is off and the profile slot becomes Sign up, as on LeagueNavBar.
function HomeNavBar({ demo = false }) {
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const { theme } = useTheme();
  const dark = theme === 'dark';
  const navigate = useNavigate();

  return (
    <>
      <div style={{
        position: 'sticky', top: 0, zIndex: 40,
        backgroundColor: BAR.background,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderBottom: BAR.border,
        paddingTop: 'env(safe-area-inset-top)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}>
        {/* Equal side columns, so the wordmark sits on the true centre of the
            bar rather than on the centre of whatever is left over. */}
        <div style={{
          display: 'grid', gridTemplateColumns: demo ? '76px 1fr 76px' : '44px 1fr 44px', alignItems: 'center',
          padding: '8px 16px', minHeight: 52,
        }}>
          {/* The page tour's replay, and otherwise a spacer holding the
              wordmark on the true centre of the bar. The theme toggle lived
              here until the appearance picker went into the settings menu
              opposite. */}
          <div style={{ display: 'flex' }}>
            <TourButton />
          </div>

          <button
            onClick={() => { if (!demo) setSwitcherOpen(true); }}
            aria-label={demo ? undefined : "Switch league"}
            disabled={demo}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              border: 'none', background: 'none', cursor: demo ? 'default' : 'pointer',
              padding: '4px 0', minHeight: 44, margin: '0 auto', maxWidth: '100%',
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {/* Picked from the resolved theme rather than a <picture> media
                query: the toggle has to move the mark too, and a media query
                cannot see it. The inline script in index.html sets the theme
                before React mounts, so this is right on the first paint. */}
            <img
              src={dark ? wordmarkOnDark : wordmarkOnLight}
              alt="Lineup CFB"
              style={{ height: 33, width: 'auto', objectFit: 'contain' }}
            />
            {!demo && <ChevronDown size={15} color={BAR.caret} style={{ flexShrink: 0 }} />}
          </button>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            {demo ? (
              <button
                data-tour="home-join"
                onClick={() => navigate('/signup')}
                style={{
                  border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                  backgroundColor: 'var(--color-cta)', color: 'var(--color-cta-text)',
                  fontWeight: 700, fontSize: 13, padding: '8px 14px', borderRadius: 50,
                }}
              >
                Sign up
              </button>
            ) : (
              <ProfileDropdown variant="icon" />
            )}
          </div>
        </div>

      </div>

      <LeagueSwitcherSheet open={switcherOpen} onClose={() => setSwitcherOpen(false)} />
    </>
  );
}

export default HomeNavBar;
